import { useState } from "react";
import { motion } from "framer-motion";
import { Play, Radio, Eye, Maximize2 } from "lucide-react";
import { gallery } from "../../data/arsenalData";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const sizeClasses = {
  hero: "md:col-span-8 md:row-span-2 aspect-[16/9] md:aspect-auto md:min-h-[420px]",
  tall: "md:col-span-4 md:row-span-2 aspect-[4/5] md:aspect-auto md:min-h-[420px]",
  square: "md:col-span-4 md:row-span-1 aspect-square md:min-h-[200px]",
};

export const Multimedia = () => {
  const [lightboxItem, setLightboxItem] = useState(null);

  return (
    <section
      id="multimedia"
      data-testid="multimedia-section"
      className="relative bg-[#061930] text-white py-24 md:py-32 overflow-hidden"
    >
      <Dialog open={lightboxItem != null} onOpenChange={(open) => !open && setLightboxItem(null)}>
        <DialogContent className="max-h-[95vh] max-w-[min(96vw,1200px)] overflow-y-auto border border-[#DB0007]/30 bg-[#061930] p-3 text-white sm:p-6">
          <DialogTitle className="sr-only">{lightboxItem?.title ?? "Imagen galería"}</DialogTitle>
          {lightboxItem && (
            <>
              <img
                src={lightboxItem.url}
                alt={lightboxItem.title}
                className="mx-auto max-h-[min(78vh,900px)] w-full max-w-full object-contain"
              />
              <p className="font-outfit mt-3 text-center text-sm text-white/80">{lightboxItem.title}</p>
              <p className="font-mono-jb text-center text-[10px] uppercase tracking-widest text-[#9C824A]">
                {lightboxItem.category}
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="absolute top-0 right-0 w-1/4 h-1 bg-[#DB0007]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="flex items-end justify-between flex-wrap gap-6 mb-14"
        >
          <div>
            <div className="section-overline">Multimedia</div>
            <h2 className="font-anton uppercase text-4xl sm:text-5xl lg:text-6xl mt-6 tracking-tight">
              Momentos <span className="text-[#DB0007]">eternos</span>
            </h2>
          </div>
          <p className="font-outfit text-white/60 max-w-sm">
            Galería y transmisión en vivo. Cada imagen cuenta una historia de esfuerzo y pasión.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 auto-rows-auto">
          {gallery.map((item, i) => (
            <motion.button
              key={item.id}
              type="button"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className={`bento-item relative group overflow-hidden bg-[#0a2240] border border-white/10 text-left hover:border-[#DB0007] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#DB0007] ${sizeClasses[item.size] || sizeClasses.square}`}
              data-testid={`gallery-item-${item.id}`}
              onClick={() => setLightboxItem(item)}
            >
              <img
                src={item.url}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#061930] via-[#061930]/40 to-transparent" />
              <div className="pointer-events-none absolute inset-0 p-5 sm:p-6 flex flex-col justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-mono-jb text-[10px] uppercase tracking-[0.3em] bg-[#DB0007] px-2 py-1 text-white">
                    {item.category}
                  </span>
                </div>
                <div>
                  <h3 className="font-anton uppercase text-xl sm:text-2xl leading-tight">
                    {item.title}
                  </h3>
                  <div className="mt-2 flex items-center gap-3 text-white/60 text-xs font-outfit opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="flex items-center gap-1">
                      <Eye size={12} /> Ver
                    </span>
                    <span className="flex items-center gap-1">
                      <Maximize2 size={12} /> Ampliar
                    </span>
                  </div>
                </div>
              </div>
            </motion.button>
          ))}

          {/* Streaming player - Special bento item */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="md:col-span-12 relative overflow-hidden bg-black border border-[#DB0007] min-h-[400px] md:min-h-[500px] group"
            data-testid="streaming-player"
          >
            <img
              src="https://images.unsplash.com/photo-1777643155941-088f4b2a8940?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxzb2NjZXIlMjBtYXRjaCUyMHN0YWRpdW0lMjBuaWdodHxlbnwwfHx8fDE3NzgwMzg4ODd8MA&ixlib=rb-4.1.0&q=85"
              alt="Streaming en vivo"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#061930]/95 via-[#061930]/70 to-transparent" />

            {/* LIVE badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2 bg-[#DB0007] px-3 py-1.5">
              <span className="w-2 h-2 bg-white rounded-full live-pulse" />
              <span className="font-anton uppercase text-white text-xs tracking-widest">En Vivo</span>
            </div>

            {/* Viewer count */}
            <div className="absolute top-4 right-4 flex items-center gap-2 bg-black/60 backdrop-blur-md px-3 py-1.5 border border-white/10">
              <Eye size={14} className="text-white/70" />
              <span className="font-mono-jb text-white text-xs">1,247 viendo</span>
            </div>

            {/* Play button center */}
            <button
              data-testid="streaming-play-button"
              className="absolute inset-0 flex items-center justify-center"
              type="button"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#DB0007] blur-2xl opacity-60 group-hover:opacity-100 transition-opacity" />
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 bg-[#DB0007] flex items-center justify-center border-4 border-white/20 group-hover:scale-110 transition-transform">
                  <Play size={32} className="text-white fill-white translate-x-1" />
                </div>
              </div>
            </button>

            {/* Content overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 pointer-events-none">
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Radio size={16} className="text-[#DB0007]" />
                    <span className="font-mono-jb text-xs uppercase tracking-[0.3em] text-[#9C824A]">
                      Arsenal TV · Streaming Live
                    </span>
                  </div>
                  <h3 className="font-anton uppercase text-2xl sm:text-4xl text-white leading-tight max-w-2xl">
                    Arsenal vs Galax
                    <br />
                    <span className="text-[#DB0007]">Jornada 5 · Primera División Local</span>
                  </h3>
                  <p className="font-outfit text-white/70 mt-3 text-sm">
                    Transmisión oficial con narración y estadísticas en tiempo real.
                  </p>
                </div>
                <div className="flex gap-2 pointer-events-auto">
                  <button type="button" className="px-4 py-2 bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 font-outfit text-sm transition-colors">
                    Calidad HD
                  </button>
                  <button type="button" className="px-4 py-2 bg-[#DB0007] font-anton uppercase text-sm tracking-widest hover:bg-[#9C824A] transition-colors">
                    Compartir
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
